import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import { auth } from '../../services/firebase';
import { setAuthToken, syncUserAccount } from '../../services/api';
import { readJSON, writeJSON } from '../../shared/storage';
import { useToast } from '../toast';

const USER_STORAGE_KEY = 'ponpon:lastUser';

const resolveStoredUser = () => {
  return readJSON(USER_STORAGE_KEY);
};

const AuthContext = createContext({
  user: null,
  authReady: false,
  loginPromptOpen: false,
  loginInitialMode: 'signup',
  login: () => {},
  logout: () => Promise.resolve(),
  requireLogin: () => {},
  skipLogin: () => {},
  resendVerification: () => Promise.resolve(),
  sendPasswordReset: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(null),
});

// CHANGED: Get base URL based on environment (staging or production)
const getBaseUrl = () => {
  // Priority 1: Use environment variable if set
  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }

  // Priority 2: Detect from window.location (for runtime detection)
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    // Check if staging subdomain
    if (hostname.includes('staging.ponpon.app') || hostname === 'staging.ponpon.app') {
      return 'https://staging.ponpon.app';
    }
    // Default to production
    if (hostname.includes('ponpon.app') || hostname === 'ponpon.app') {
      return 'https://ponpon.app';
    }
    // For local development, use current origin
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${window.location.protocol}//${window.location.host}`;
    }
  }

  // Priority 3: Fallback to production
  return 'https://ponpon.app';
};

// CHANGED: Unified action URL for all auth actions (email verification, password reset, etc.)
// Setting handleCodeInApp: false ensures Firebase redirects directly to our custom page,
// skipping the default Firebase action handler page
const getActionCodeSettings = (mode = 'verifyEmail') => ({
  url: `${getBaseUrl()}/auth/action?mode=${mode}`,
  handleCodeInApp: false, // This ensures we skip Firebase's default page and go directly to our custom page
});

const AuthProvider = ({ children }) => {
  const storedUser = useMemo(() => resolveStoredUser(), []);
  const [user, setUserState] = useState(storedUser);
  // CHANGED: Start with authReady=true so pages can render immediately
  // Auth hydration happens in background
  const [authReady, setAuthReady] = useState(true);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [loginInitialMode, setLoginInitialMode] = useState('signup');
  const previousVerifiedRef = useRef(null);
  const { showToast, dismissToast } = useToast();

  const setUser = useCallback((nextValue) => {
    setUserState((previous) => {
      const resolvedValue =
        typeof nextValue === 'function' ? nextValue(previous) : nextValue;
      const normalized = resolvedValue ?? null;
      writeJSON(USER_STORAGE_KEY, normalized);
      return normalized;
    });
  }, []);

  const login = useCallback(
    (userData) => {
      setUser(userData);
      setLoginPromptOpen(false);
      dismissToast();
    },
    [dismissToast, setUser],
  );

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setAuthToken(null);
      setUser(null);
      previousVerifiedRef.current = null;
    } catch (error) {
      console.error('Failed to sign out:', error);
      showToast({
        message: 'Unable to sign out right now. Please try again.',
        tone: 'error',
      });
      throw error;
    }
  }, [setUser, showToast]);

  const skipLogin = useCallback(() => {
    setLoginPromptOpen(false);
    dismissToast();
  }, [dismissToast]);

  const resendVerification = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return;
    }

    try {
      await currentUser.reload();
      const actionCodeSettings = getActionCodeSettings('verifyEmail');
      console.log('Sending verification email with settings:', {
        url: actionCodeSettings.url,
        handleCodeInApp: actionCodeSettings.handleCodeInApp,
        email: currentUser.email,
      });
      await sendEmailVerification(currentUser, actionCodeSettings);
      console.log('Verification email sent successfully');
      showToast({
        message: 'Verification email sent! Please check your inbox.',
        tone: 'success',
      });
    } catch (error) {
      console.error('Failed to resend verification:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        actionCodeSettings: getActionCodeSettings('verifyEmail'),
        baseUrl: getBaseUrl(),
      });
      
      // CHANGED: Provide specific error message for unauthorized domain
      let errorMessage = 'Unable to resend verification email. Please try again.';
      if (error.code === 'auth/unauthorized-continue-uri') {
        errorMessage = `Domain not authorized. Please add "${getBaseUrl().replace(/^https?:\/\//, '')}" to Firebase Authorized domains.`;
        console.error('SOLUTION: Go to Firebase Console > Authentication > Settings > Authorized domains and add:', getBaseUrl().replace(/^https?:\/\//, ''));
      }
      
      showToast({
        message: errorMessage,
        tone: 'error',
      });
    }
  }, [showToast]);

  const sendPasswordReset = useCallback(
    async (email) => {
      if (!email) {
        return;
      }
      try {
        // CHANGED: Add actionCodeSettings for password reset to use unified handler
        const actionCodeSettings = getActionCodeSettings('resetPassword');
        console.log('Sending password reset email with settings:', {
          url: actionCodeSettings.url,
          handleCodeInApp: actionCodeSettings.handleCodeInApp,
          email,
        });
        await sendPasswordResetEmail(auth, email, actionCodeSettings);
        console.log('Password reset email sent successfully');
        showToast({
          message: 'Password reset email sent! Please check your inbox.',
          tone: 'success',
        });
      } catch (error) {
        console.error('Failed to send password reset email:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          actionCodeSettings: getActionCodeSettings('resetPassword'),
          baseUrl: getBaseUrl(),
        });
        
        // CHANGED: Provide specific error message for unauthorized domain
        let errorMessage = 'Unable to send password reset email. Please try again.';
        if (error.code === 'auth/unauthorized-continue-uri') {
          errorMessage = `Domain not authorized. Please add "${getBaseUrl().replace(/^https?:\/\//, '')}" to Firebase Authorized domains.`;
          console.error('SOLUTION: Go to Firebase Console > Authentication > Settings > Authorized domains and add:', getBaseUrl().replace(/^https?:\/\//, ''));
        }
        
        showToast({
          message: errorMessage,
          tone: 'error',
        });
        throw error;
      }
    },
    [showToast],
  );

  const refreshUser = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return null;
    }
    try {
      await firebaseUser.reload();
      const token = await firebaseUser.getIdToken(true);
      setAuthToken(token);
      const response = await syncUserAccount({
        displayName: firebaseUser.displayName || undefined,
        email: firebaseUser.email || undefined,
        photoURL: firebaseUser.photoURL || undefined,
      });
      setUser(response.user);
      return response.user;
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      showToast({
        message: 'Unable to refresh your account. Please try again.',
        tone: 'error',
      });
      throw error;
    }
  }, [setUser, showToast]);

  const requireLogin = useCallback(
    (message = null, mode = null) => {
      const currentUser = auth.currentUser;
      if (currentUser && !currentUser.emailVerified) {
        showToast({
          message: (
            <>
              Verify your email to unlock all features.&nbsp;
              <button
                type="button"
                onClick={resendVerification}
                className="font-semibold underline underline-offset-2 hover:opacity-80"
              >
                Resend.
              </button>
            </>
          ),
          tone: 'info',
        });
        return;
      }

      if (typeof message === 'string' && message.trim().length > 0) {
        showToast({ message: message.trim(), tone: 'error' });
      }

      const fallbackMode =
        currentUser && !currentUser.emailVerified ? 'signin' : 'signup';
      setLoginInitialMode(mode || fallbackMode);
      setLoginPromptOpen(true);
    },
    [resendVerification, showToast],
  );

  // Reverted to popup: subscribe to onAuthStateChanged only (Google result handled in UserLogin)
  useEffect(() => {
    let isMounted = true;

    const handleAuthState = async (firebaseUser) => {
      if (!isMounted) return;

      if (firebaseUser) {
        try {
          await firebaseUser.reload();
        } catch (error) {
          // Swallow reload errors; keep going with stale data.
        }

        const isVerified = Boolean(firebaseUser.emailVerified);
        try {
          const token = await firebaseUser.getIdToken();
          setAuthToken(token);
        } catch (error) {
          console.error('Failed to obtain Firebase token:', error);
        }

        try {
          const response = await syncUserAccount({
            displayName: firebaseUser.displayName || undefined,
            email: firebaseUser.email || undefined,
            photoURL: firebaseUser.photoURL || undefined,
          });
          setUser(response.user);
          if (isVerified && previousVerifiedRef.current === false) {
            showToast({
              message: 'Email verified! Welcome 🎉',
              tone: 'success',
            });
          } else {
            dismissToast();
          }
          previousVerifiedRef.current = isVerified;
        } catch (error) {
          console.error('Failed to sync Firebase user with backend:', error);
          showToast({
            message: 'Failed to sync your account. Please try again.',
            tone: 'error',
          });
        }
      } else {
        setAuthToken(null);
        setUser(null);
        previousVerifiedRef.current = null;
        dismissToast();
      }
      setAuthReady(true);
    };

    const unsub = onAuthStateChanged(auth, handleAuthState);
    return () => {
      isMounted = false;
      if (typeof unsub === 'function') unsub();
    };
  }, [dismissToast, setUser, showToast]);

  const value = useMemo(
    () => ({
      user,
      authReady,
      loginPromptOpen,
      loginInitialMode,
      login,
      logout,
      requireLogin,
      skipLogin,
      resendVerification,
      sendPasswordReset,
      refreshUser,
      setLoginPromptOpen,
      setLoginInitialMode,
    }),
    [
      user,
      authReady,
      loginPromptOpen,
      loginInitialMode,
      login,
      logout,
      requireLogin,
      skipLogin,
      resendVerification,
      sendPasswordReset,
      refreshUser,
      setLoginPromptOpen,
      setLoginInitialMode,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
